import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';

interface Recording {
  id: string;
  title: string;
  venueName?: string;
  venue_name?: string;
  startTime?: string;
  start_time?: string;
  created_at?: string;
  streamingUrl?: string;
  streaming_url?: string;
  durationSeconds?: number;
  duration_seconds?: number;
  waveformData?: number[];
  [key: string]: any;
}

interface AudioPlayerState {
  currentRecording: Recording | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLoading: boolean;
}

interface AudioPlayerContextType extends AudioPlayerState {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  play: (recording: Recording) => Promise<void>;
  pause: () => void;
  togglePlayPause: () => Promise<void>;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  skip: (seconds: number) => void;
  stop: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [state, setState] = useState<AudioPlayerState>({
    currentRecording: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: false,
    isLoading: false,
  });

  // Audio element event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setState(prev => ({ ...prev, currentTime: audio.currentTime }));
    };

    const handleDurationChange = () => {
      setState(prev => ({ ...prev, duration: audio.duration }));
    };

    const handleEnded = () => {
      setState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }));
    };

    const handleLoadStart = () => {
      setState(prev => ({ ...prev, isLoading: true }));
    };

    const handleCanPlay = () => {
      setState(prev => ({ ...prev, isLoading: false }));
    };

    const handleError = (e: Event) => {
      console.error('Audio playback error:', e);
      setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  // Sync audio element volume with state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.isMuted ? 0 : state.volume;
    }
  }, [state.volume, state.isMuted]);

  const play = async (recording: Recording) => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      // If same recording, just resume
      if (state.currentRecording?.id === recording.id && !state.isPlaying) {
        await audio.play();
        setState(prev => ({ ...prev, isPlaying: true }));
        return;
      }

      // New recording - load and play
      setState(prev => ({
        ...prev,
        currentRecording: recording,
        isPlaying: false,
        isLoading: true,
        currentTime: 0
      }));

      audio.src = recording.streamingUrl || recording.streaming_url || '';
      audio.load();
      await audio.play();
      setState(prev => ({ ...prev, isPlaying: true }));
    } catch (error) {
      console.error('Error playing audio:', error);
      setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
    }
  };

  const pause = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      setState(prev => ({ ...prev, isPlaying: false }));
    }
  };

  const togglePlayPause = async () => {
    if (state.isPlaying) {
      pause();
    } else if (state.currentRecording) {
      await play(state.currentRecording);
    }
  };

  const seek = (time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setState(prev => ({ ...prev, currentTime: time }));
    }
  };

  const setVolume = (volume: number) => {
    setState(prev => ({ ...prev, volume, isMuted: volume === 0 }));
  };

  const toggleMute = () => {
    setState(prev => ({ ...prev, isMuted: !prev.isMuted }));
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = Math.max(0, Math.min(state.duration, audio.currentTime + seconds));
      seek(newTime);
    }
  };

  const stop = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setState(prev => ({
      ...prev,
      currentRecording: null,
      isPlaying: false,
      currentTime: 0,
    }));
  };

  const value: AudioPlayerContextType = {
    ...state,
    audioRef,
    play,
    pause,
    togglePlayPause,
    seek,
    setVolume,
    toggleMute,
    skip,
    stop,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="metadata"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};
